/**
 * The co-pilot's moving parts, in one hook: load what we know, tap the call,
 * upload a window at a time, fold each answer into what the panel draws.
 *
 * Two things about the shape are deliberate.
 *
 * The snapshot is loaded as soon as there is a call log id, which is BEFORE the
 * phone is answered. What our scanner found on their site is at its most useful
 * in the seconds while it is still ringing, and there is nothing to wait for.
 *
 * A window's answer arrives in the response to its own upload, so the normal
 * path needs no polling at all. A `GET` is only spent to catch up after a window
 * that failed - which keeps the request count at roughly one per window instead
 * of one per window plus one every few seconds for the length of the call.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  endPilotSession,
  getPilotSnapshot,
  uploadPilotWindow,
  type PilotChecklistItem,
  type PilotFact,
  type PilotSnapshot,
  type PilotState,
  type PilotTurn,
} from "../../../lib/callPilot";
import { clampWindowMs, isCaptureSupported, startLiveCapture, type CapturedWindow, type LiveCaptureHandle } from "./capture";

/** Windows allowed to wait for their turn before the oldest is dropped. */
const MAX_QUEUE = 2;

/** How long to keep asking the SDK for the remote stream after the call is up. */
const REMOTE_WAIT_MS = 4_000;
const REMOTE_POLL_MS = 250;

export type PilotStatus =
  | "loading"
  | "off"
  | "waiting"
  | "listening"
  | "budget_spent"
  /** The account's daily AI budget is spent, or the co-pilot's share of it. */
  | "quota_spent"
  | "no_audio"
  /** The call is on hold - nothing to hear, and nothing spent hearing it. */
  | "on_hold"
  | "failed";

export interface LivePilot {
  status: PilotStatus;
  /** Machine detail behind `failed` / `no_audio`, for the panel's message picker. */
  reason: string | null;
  /** Why `off`, when it is off: the switch, the key, or the missing notice. */
  blocker: PilotSnapshot["blocker"];
  turns: PilotTurn[];
  state: PilotState;
  /**
   * The stored checklist, so the panel has real questions to show before the
   * model has answered once. It was already in the snapshot and thrown away,
   * which is how a failed first window ended up looking like an empty agenda.
   */
  checklist: PilotChecklistItem[];
  facts: PilotFact[];
  hadContact: boolean;
  scanUncertain: boolean;
  windows: number;
  maxWindows: number;
  /** Windows captured but not yet answered for - drives the "listening" pulse. */
  pending: number;
}

const EMPTY_STATE: PilotState = {
  answered: [],
  open: [],
  highlights: [],
  guides: [],
  nextSteps: [],
  suggestedAtSeq: -1,
};

function mergeTurns(previous: PilotTurn[], incoming: PilotTurn[]): PilotTurn[] {
  if (incoming.length === 0) return previous;
  const seen = new Set(previous.map((t) => `${t.seq}|${t.speaker}|${t.text}`));
  const added = incoming.filter((t) => !seen.has(`${t.seq}|${t.speaker}|${t.text}`));
  if (added.length === 0) return previous;
  // Sorted by window, so a window that answered out of order still reads in the
  // order it was spoken.
  return [...previous, ...added].sort((a, b) => a.seq - b.seq);
}

export interface UseLivePilotOptions {
  callLogId: string | null;
  /** True while the call is actually connected - the only phase worth tapping. */
  active: boolean;
  /** The panel is closed, so there is nothing to listen for. */
  enabled: boolean;
  /**
   * The customer is on hold. The tap keeps its graph and its window numbering
   * but stops cutting windows: a held customer is removed from the mix, so the
   * far channel is silence and every window spent on it comes out of the same
   * per-call budget a real conversation needs.
   */
  paused?: boolean;
  getStreams: () => { local: MediaStream | null; remote: MediaStream | null } | null;
}

export function useLivePilot({ callLogId, active, enabled, paused = false, getStreams }: UseLivePilotOptions): LivePilot {
  const [snapshot, setSnapshot] = useState<PilotSnapshot | null>(null);
  const [turns, setTurns] = useState<PilotTurn[]>([]);
  const [state, setState] = useState<PilotState>(EMPTY_STATE);
  const [facts, setFacts] = useState<PilotFact[]>([]);
  const [windows, setWindows] = useState(0);
  const [pending, setPending] = useState(0);
  const [status, setStatus] = useState<PilotStatus>("loading");
  const [reason, setReason] = useState<string | null>(null);

  const captureRef = useRef<LiveCaptureHandle | null>(null);
  const queueRef = useRef<CapturedWindow[]>([]);
  /**
   * Windows binned since the server last heard from us.
   *
   * A ref rather than state on purpose: nothing draws it, and re-rendering the
   * panel because audio was discarded would be a render on the exact call where
   * the machine is already behind. It rides along with the next upload, which
   * is the only way the server can ever learn that a window existed - see
   * `lib/pilot/latency.ts`.
   */
  const droppedRef = useRef(0);
  const sendingRef = useRef(false);
  const idRef = useRef<string | null>(null);
  idRef.current = callLogId;

  // ── What we already know ──────────────────────────────────────────────────
  useEffect(() => {
    if (!callLogId || !enabled) return;
    let live = true;
    setStatus("loading");
    getPilotSnapshot(callLogId)
      .then((data) => {
        if (!live) return;
        setSnapshot(data);
        setTurns(data.turns);
        setState(data.state);
        setFacts(data.facts);
        setWindows(data.windows);
        setStatus(data.ready ? "waiting" : "off");
      })
      .catch(() => {
        if (!live) return;
        setStatus("failed");
        setReason("snapshot");
      });
    return () => {
      live = false;
    };
  }, [callLogId, enabled]);

  // ── One window at a time, oldest first ────────────────────────────────────
  const drain = useCallback(async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      for (;;) {
        const next = queueRef.current.shift();
        const id = idRef.current;
        if (!next || !id) break;
        setPending(queueRef.current.length + 1);
        const dropped = droppedRef.current;
        try {
          const result = await uploadPilotWindow(id, next, undefined, dropped);
          // Subtracted rather than zeroed: `onWindow` fires while this upload is
          // in flight, so more windows may have been binned since `dropped` was
          // read. Zeroing here would forget exactly the drops that happened
          // during the slow request that caused them.
          droppedRef.current -= dropped;
          setWindows(result.windows);
          if (result.facts.length > 0) setFacts(result.facts);
          if (result.ok) {
            setTurns((prev) => mergeTurns(prev, result.turns));
            setState(result.state);
            setStatus("listening");
            setReason(null);
          } else if (result.error === "quota_spent") {
            // Torn down like `budget_spent` rather than retried: the answer
            // changes at midnight, and asking again every fifteen seconds would
            // spend the day arguing with a closed door.
            setStatus("quota_spent");
          } else if (result.error === "budget_spent") {
            setStatus("budget_spent");
            // The ceiling is not a failure to recover from: stop capturing
            // rather than keep cutting windows nothing will accept.
            captureRef.current?.stop();
            captureRef.current = null;
            queueRef.current = [];
          } else if (result.error === "disabled" || result.error === "no_notice") {
            // Both are the server refusing to listen rather than failing to:
            // the panel says "off" and takes the reason from the snapshot's
            // blocker, which names which of the two silences this is.
            setStatus("off");
          } else if (result.error === "recording_stopped") {
            // Somebody asked us to stop recording, and this is the tap being
            // told the same thing. Handled like the budget rather than like a
            // refusal: capture STOPS, because every further window would be
            // audio from a person who has said no travelling to a server to be
            // refused. `off` on its own would leave the tap running.
            setStatus("off");
            setReason(null);
            captureRef.current?.stop();
            captureRef.current = null;
            queueRef.current = [];
          } else if (result.error === "on_hold") {
            // Explicitly, and not through the `failed` branch below: a window
            // refused because the call is on hold is the server working, and
            // the red "the model broke" hint would be the opposite of true.
            setStatus("on_hold");
            setReason(null);
          } else {
            setStatus("failed");
            setReason(result.detail ?? result.error ?? "model_failed");
          }
        } catch {
          // A window that did not make it is gone. Re-reading what the server
          // does hold keeps the panel honest instead of leaving a silent hole.
          setStatus("failed");
          setReason("upload");
          await getPilotSnapshot(id, -1)
            .then((data) => {
              setTurns((prev) => mergeTurns(prev, data.turns));
              setState(data.state);
              setWindows(data.windows);
            })
            .catch(() => {});
        }
      }
    } finally {
      sendingRef.current = false;
      setPending(queueRef.current.length);
    }
  }, []);

  // ── The tap ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const ready = snapshot?.ready === true;
    if (!callLogId || !enabled || !active || !ready) return;
    if (!isCaptureSupported()) {
      setStatus("no_audio");
      setReason("unsupported");
      return;
    }

    let cancelled = false;
    let handle: LiveCaptureHandle | null = null;

    void (async () => {
      // The SDK publishes the remote stream a moment after the call is accepted,
      // so an immediate read is legitimately empty. Waiting a few seconds beats
      // capturing a call with one silent side.
      const deadline = Date.now() + REMOTE_WAIT_MS;
      let streams = getStreams();
      while (!cancelled && (!streams?.remote || !streams.local) && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, REMOTE_POLL_MS));
        streams = getStreams();
      }
      if (cancelled) return;
      if (!streams?.local) {
        setStatus("no_audio");
        setReason("no_local_stream");
        return;
      }

      try {
        handle = await startLiveCapture({
          local: streams.local,
          remote: streams.remote,
          windowMs: clampWindowMs((snapshot?.windowSec ?? 15) * 1000),
          // Continue the server's numbering rather than restarting it: a panel
          // closed and reopened mid-call would otherwise file its new windows in
          // front of everything already said.
          startSeq: snapshot?.windows ?? 0,
          onWindow: (window) => {
            queueRef.current.push(window);
            // A backlog means the model is slower than the call. Dropping the
            // OLDEST keeps the panel near the present, which is the only place
            // it is any use; keeping it would show a transcript falling behind.
            //
            // ⚠ COUNTED, because this is the one thing no server-side clock can
            // ever see. A window binned here never becomes a request, so the
            // latency the server measures is the latency of the windows that
            // were fast enough to survive - which is a flattering average taken
            // on the exact call where the desk fell apart.
            while (queueRef.current.length > MAX_QUEUE) {
              queueRef.current.shift();
              droppedRef.current += 1;
            }
            void drain();
          },
          onError: (code) => {
            // A missing far end is a warning, not a stop: half a transcript
            // still records what the operator asked.
            if (code === "no_remote_stream") {
              setReason(code);
              return;
            }
            setStatus("no_audio");
            setReason(code);
          },
        });
        if (cancelled) {
          handle.stop();
          return;
        }
        captureRef.current = handle;
        setStatus((prev) => (prev === "waiting" || prev === "loading" ? "listening" : prev));
      } catch {
        // startLiveCapture has already reported the specific code through onError.
        if (!cancelled) setStatus("no_audio");
      }
    })();

    return () => {
      cancelled = true;
      // The tail window is flushed by stop(), so the last thing said before
      // hanging up still reaches the panel.
      handle?.stop();
      captureRef.current = null;
      void drain();
    };
  }, [callLogId, enabled, active, snapshot?.ready, snapshot?.windowSec, getStreams, drain]);

  // Pause the tap while the customer is on hold - a separate effect rather than
  // a dependency of the one above, because restarting the capture would renumber
  // the windows: `startSeq` is read once from the snapshot at start, and the
  // server already holds windows under those numbers.
  useEffect(() => {
    captureRef.current?.setPaused(paused);
    if (paused) setStatus((prev) => (prev === "listening" ? "on_hold" : prev));
    else setStatus((prev) => (prev === "on_hold" ? "listening" : prev));
  }, [paused]);

  // The call is over: the server can drop the context it cached for it. Fire and
  // forget - a cache entry that outlives its call expires on its own anyway.
  useEffect(() => {
    if (!callLogId || active) return;
    return () => {
      void endPilotSession(callLogId).catch(() => {});
    };
  }, [callLogId, active]);

  return {
    status,
    reason,
    blocker: snapshot?.blocker ?? null,
    turns,
    state,
    checklist: snapshot?.checklist ?? [],
    facts,
    hadContact: snapshot?.hadContact ?? false,
    scanUncertain: snapshot?.scanUncertain ?? false,
    windows,
    maxWindows: snapshot?.maxWindows ?? 0,
    pending,
  };
}
